import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
} from '@mui/material';
import { CalendarMonth, LinkOff } from '@mui/icons-material';
import {
  useGoogleCalendarStatus,
  useToggleGoogleCalendar,
  useDisconnectGoogleCalendar,
} from '../../hooks/useGoogleCalendar';
import { googleCalendarApi } from '../../services/googleCalendar';

export default function GoogleCalendarConnect() {
  const { data: status, isLoading } = useGoogleCalendarStatus();
  const toggleMutation = useToggleGoogleCalendar();
  const disconnectMutation = useDisconnectGoogleCalendar();

  const [searchParams, setSearchParams] = useSearchParams();
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);
  const [connecting, setConnecting] = useState(false);

  // Handle OAuth callback query params
  useEffect(() => {
    const gcalResult = searchParams.get('gcal');
    if (gcalResult === 'success') {
      setToast({ type: 'success', message: 'Google Calendar connected successfully!' });
      // Clean up URL
      searchParams.delete('gcal');
      setSearchParams(searchParams, { replace: true });
    } else if (gcalResult === 'error') {
      const reason = searchParams.get('reason') || 'unknown';
      setToast({
        type: 'error',
        message: reason === 'access_denied'
          ? 'Google Calendar access was denied. Please try again.'
          : 'Failed to connect Google Calendar. Please try again.',
      });
      searchParams.delete('gcal');
      searchParams.delete('reason');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Auto-dismiss toast
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(timer);
  }, [toast]);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const url = await googleCalendarApi.getConnectUrl();
      window.location.href = url;
    } catch {
      setToast({ type: 'error', message: 'Failed to start Google Calendar connection.' });
      setConnecting(false);
    }
  };

  const handleToggle = async () => {
    try {
      await toggleMutation.mutateAsync();
    } catch {
      setToast({ type: 'error', message: 'Failed to toggle sync.' });
    }
  };

  const handleDisconnect = async () => {
    try {
      await disconnectMutation.mutateAsync();
      setDisconnectDialogOpen(false);
      setToast({ type: 'success', message: 'Google Calendar disconnected.' });
    } catch {
      setToast({ type: 'error', message: 'Failed to disconnect Google Calendar.' });
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} />
        </CardContent>
      </Card>
    );
  }

  const isConnected = status?.connected ?? false;
  const isActive = status?.isActive ?? false;

  return (
    <>
      {toast && (
        <Alert
          severity={toast.type}
          sx={{ mb: 2 }}
          onClose={() => setToast(null)}
        >
          {toast.message}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <CalendarMonth sx={{ color: '#4285F4', fontSize: 28 }} />
              <Typography variant="h6">Google Calendar</Typography>
            </Box>
            {isConnected && (
              <Chip
                label="Connected"
                size="small"
                sx={{
                  bgcolor: isActive ? '#E8F5E9' : '#FFF3E0',
                  color: isActive ? '#2E7D32' : '#E65100',
                  fontWeight: 600,
                }}
              />
            )}
          </Box>

          {!isConnected ? (
            // Disconnected State
            <>
              <Typography variant="body2" color="text.secondary" mb={3}>
                Sync your Elevair bookings to your Google Calendar automatically. New bookings
                will appear as events, and cancellations will be removed.
              </Typography>
              <Button
                variant="contained"
                startIcon={<CalendarMonth />}
                onClick={handleConnect}
                disabled={connecting}
              >
                {connecting ? 'Connecting...' : 'Connect Google Calendar'}
              </Button>
            </>
          ) : (
            // Connected State
            <>
              <Typography variant="body2" color="text.secondary" mb={1}>
                Connected as: <strong>{status?.googleEmail}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                New bookings will appear on your Google Calendar automatically.
              </Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={isActive}
                    onChange={handleToggle}
                    disabled={toggleMutation.isPending}
                  />
                }
                label="Sync is active"
                sx={{ mb: 2, display: 'block' }}
              />

              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<LinkOff />}
                onClick={() => setDisconnectDialogOpen(true)}
              >
                Disconnect
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      {/* Disconnect Confirmation Dialog */}
      <Dialog open={disconnectDialogOpen} onClose={() => setDisconnectDialogOpen(false)}>
        <DialogTitle>Disconnect Google Calendar?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will stop syncing new bookings to your Google Calendar. Existing calendar
            events will not be removed. You can reconnect at any time.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisconnectDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDisconnect}
            color="error"
            disabled={disconnectMutation.isPending}
          >
            {disconnectMutation.isPending ? 'Disconnecting...' : 'Disconnect'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
